import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import type { SvgIcon } from '@mui/material';
import { MousePointerClick } from 'lucide-react';
import { forwardRef, type MouseEvent, type ReactNode } from 'react';
import { ICON_BUTTON_PX, ICON_PX } from '~/theme/iconSizes';

/**
 * The card that ends the sidebar's framed panel with the details of the
 * selected object: a raised fill under a divider, its bottom corners
 * following the frame's. Its children stack 4 px apart.
 */
export const DetailsCard = forwardRef<
    HTMLDivElement,
    { children: ReactNode; 'data-testid'?: string }
>(function DetailsCard({ children, 'data-testid': testId }, ref) {
    return (
        <Box
            ref={ref}
            data-testid={testId}
            sx={(theme) => ({
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                minWidth: 0,
                pt: 1,
                pr: 1.25,
                pb: 1.5,
                pl: 2,
                bgcolor: 'background.raised',
                borderTop: 1,
                borderColor: 'divider',
                // The fill follows the frame's rounded bottom corners,
                // inside the frame's 1 px border.
                borderBottomLeftRadius: `${theme.shape.borderRadius - 1}px`,
                borderBottomRightRadius: `${theme.shape.borderRadius - 1}px`,
            })}
        >
            {children}
        </Box>
    );
});

/**
 * A {@link DetailsCard} with nothing selected yet: a pointer and a line that
 * says what to select to fill it. It stands where the card opens, so it
 * reads as that card waiting for a selection.
 */
export function DetailsCardHint({
    children,
    'data-testid': testId = 'details-card-hint',
}: {
    /** What to select, and what selecting it opens. */
    children: ReactNode;
    'data-testid'?: string;
}) {
    return (
        <DetailsCard data-testid={testId}>
            {/* The card's top padding expects a 28 px header row; with
                none, the hint adds the rest, so it sits as far from the
                divider as from the card's bottom. */}
            <Box display="flex" alignItems="flex-start" gap={1} pt={0.5}>
                <Box
                    component="span"
                    display="flex"
                    flexShrink={0}
                    color="primary.main"
                >
                    <MousePointerClick size={ICON_PX} aria-hidden />
                </Box>
                <Typography fontSize={12} lineHeight="16px">
                    {children}
                </Typography>
            </Box>
        </DetailsCard>
    );
}

/** The 28 px header row of a {@link DetailsCard}: the name, then the actions. */
export function DetailsCardHeader({ children }: { children: ReactNode }) {
    return (
        <Box
            display="flex"
            alignItems="center"
            gap={0.5}
            height={28}
            minWidth={0}
            mr={-0.5}
        >
            {children}
        </Box>
    );
}

/** A 24 px action of a card's header, its tooltip naming it. */
export function CardHeaderAction({
    Icon,
    label,
    onClick,
    selected = false,
    opensMenu = false,
    disabled = false,
    disabledTooltip,
    'data-testid': testId,
}: {
    Icon: typeof SvgIcon;
    label: string;
    onClick: (event: MouseEvent<HTMLElement>) => void;
    /** Shown open: pressed, or for a menu button, its menu open. */
    selected?: boolean;
    /** The action opens a menu, which `selected` says is open. */
    opensMenu?: boolean;
    disabled?: boolean;
    /** The tooltip of the disabled action, saying why. */
    disabledTooltip?: ReactNode;
    'data-testid'?: string;
}) {
    return (
        <Tooltip title={disabled && disabledTooltip ? disabledTooltip : label}>
            {/* A disabled button fires no pointer events: the span carries
                the tooltip that says why. */}
            <Box component="span" display="flex" flexShrink={0}>
                <IconButton
                    size="small"
                    onClick={onClick}
                    disabled={disabled}
                    aria-label={label}
                    {...(opensMenu
                        ? { 'aria-haspopup': 'menu', 'aria-expanded': selected }
                        : { 'aria-pressed': selected || undefined })}
                    data-testid={testId}
                    sx={{
                        width: ICON_BUTTON_PX,
                        height: ICON_BUTTON_PX,
                        p: 0,
                        color: selected ? 'primary.main' : 'text.primary',
                        bgcolor: selected ? 'action.selected' : undefined,
                    }}
                >
                    <Icon sx={{ fontSize: ICON_PX }} />
                </IconButton>
            </Box>
        </Tooltip>
    );
}
